import { TooltipProvider } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import {
  formatPostFlightMoney,
  formatPostFlightPctBetter,
} from "../../../lib/post-flight-opportunity-metrics.js";
import type { PersonaOpportunityDisplayRow } from "../../../lib/persona-opportunity-row-model.js";
import { personaOpportunityStatusLabel } from "../../../lib/persona-opportunity-tab-status.js";
import { PersonaOpportunitySupportingMedia } from "../../shared/PersonaOpportunitySupportingMedia/PersonaOpportunitySupportingMedia.js";
import { ExpandableCellText } from "./persona-opportunity-cells.js";

const TH =
  "whitespace-nowrap px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground";
const TD = "px-3 py-3 align-middle text-[13px] text-foreground";

/** Persona table chrome with winning-ad columns. Market and competitor columns stay off this sheet. */
export function PostFlightOpportunityTable({ rows }: { rows: PersonaOpportunityDisplayRow[] }) {
  const { t } = useTranslation();
  const missing = t("personaOpportunities.postFlightOpportunity.missingValue", "—");
  const dash = (value: string | null) => value ?? missing;
  const trend = (row: PersonaOpportunityDisplayRow) => {
    if (row.postFlight?.trend === "improving") {
      return t("personaOpportunities.postFlightOpportunity.metrics.trendImproving", "Improving");
    }
    if (row.postFlight?.trend === "worsening") {
      return t("personaOpportunities.postFlightOpportunity.metrics.trendWorsening", "Worsening");
    }
    if (row.postFlight?.trend === "flat") {
      return t("personaOpportunities.postFlightOpportunity.metrics.trendFlat", "Flat");
    }
    return null;
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full min-w-[1100px] border-collapse">
          <thead className="bg-muted/30">
            <tr className="border-b border-border">
              <th className={TH} scope="col">{t("personaOpportunities.active.columns.status", "Status")}</th>
              <th className={TH} scope="col">{t("personaOpportunities.active.columns.video", "Video")}</th>
              <th className={`${TH} min-w-[200px]`} scope="col">
                {t("personaOpportunities.postFlightOpportunity.columns.title", "Title")}
              </th>
              <th className={`${TH} min-w-[220px]`} scope="col">
                {t("personaOpportunities.postFlightOpportunity.columns.direction", "Direction")}
              </th>
              <th className={TH} scope="col">
                {t("personaOpportunities.postFlightOpportunity.columns.parent", "Concept")}
              </th>
              <th className={TH} scope="col">{t("personaOpportunities.postFlightOpportunity.metrics.cpa", "CPA")}</th>
              <th className={TH} scope="col">
                {t("personaOpportunities.postFlightOpportunity.metrics.accountCpa", "Account CPA")}
              </th>
              <th className={TH} scope="col">
                {t("personaOpportunities.postFlightOpportunity.metrics.pctBetter", "% better")}
              </th>
              <th className={TH} scope="col">
                {t("personaOpportunities.postFlightOpportunity.metrics.purchases", "Purchases")}
              </th>
              <th className={TH} scope="col">{t("personaOpportunities.postFlightOpportunity.metrics.spend", "Spend")}</th>
              <th className={TH} scope="col">{t("personaOpportunities.postFlightOpportunity.metrics.rank", "Rank")}</th>
              <th className={TH} scope="col">{t("personaOpportunities.postFlightOpportunity.metrics.trend", "Trend")}</th>
              <th className={`${TH} min-w-[220px]`} scope="col">{t("personaOpportunities.active.columns.why", "Why")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const metrics = row.postFlight;
              return (
                <tr key={row.id} className="border-b border-border/70 transition-colors hover:bg-muted/40">
                  <td className={TD}>
                    <span className="inline-flex rounded-sm border border-input bg-background px-1.5 py-0.5 text-[11px] font-medium">
                      {personaOpportunityStatusLabel(t, row.status)}
                    </span>
                  </td>
                  <td className={TD}>
                    <PersonaOpportunitySupportingMedia row={row} variant="table" />
                  </td>
                  <td className={`${TD} max-w-[240px] font-semibold`}>
                    <ExpandableCellText text={row.angle} className="line-clamp-3 whitespace-normal break-words" />
                  </td>
                  <td className={`${TD} max-w-[280px] text-muted-foreground`}>
                    <ExpandableCellText
                      text={metrics?.instruction ?? missing}
                      className="line-clamp-3 whitespace-normal break-words"
                    />
                  </td>
                  <td className={`${TD} text-muted-foreground`}>{row.fromLabel ?? missing}</td>
                  <td className={`${TD} tabular-nums`}>{dash(metrics?.cpa == null ? null : formatPostFlightMoney(metrics.cpa))}</td>
                  <td className={`${TD} tabular-nums`}>
                    {dash(metrics?.accountCpa == null ? null : formatPostFlightMoney(metrics.accountCpa))}
                  </td>
                  <td className={`${TD} tabular-nums`}>
                    {dash(metrics?.pctBetter == null ? null : formatPostFlightPctBetter(metrics.pctBetter))}
                  </td>
                  <td className={`${TD} tabular-nums`}>
                    {dash(metrics?.purchases == null ? null : Math.round(metrics.purchases).toLocaleString("en-US"))}
                  </td>
                  <td className={`${TD} tabular-nums`}>
                    {dash(metrics?.spend == null ? null : formatPostFlightMoney(metrics.spend))}
                  </td>
                  <td className={`${TD} tabular-nums`}>
                    {dash(metrics?.adRank == null ? null : `#${Math.round(metrics.adRank)}`)}
                  </td>
                  <td className={TD}>{trend(row) ?? missing}</td>
                  <td className={`${TD} max-w-[280px] text-muted-foreground`}>
                    <ExpandableCellText text={row.why || missing} className="line-clamp-3 whitespace-normal break-words" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </TooltipProvider>
  );
}
